/**
 * The one Firebase app, and the one Firestore instance.
 *
 * THE IMPORT RULE: `firebase/firestore`, never `firebase/firestore/lite`. The
 * lite SDK has no local cache, no listeners and no write queue — which is the
 * entire product here. This app is used in a garage beside a whelping box where
 * there is no signal, and a weight typed in there must be on the screen
 * immediately and in Firestore whenever the phone next sees a network. That is
 * what the full SDK's persistent cache does and what lite cannot be made to do.
 *
 * It costs roughly 100 KB gzipped over lite. That is the price of the feature.
 *
 * `persistentMultipleTabManager` rather than the single-tab default: the app is
 * installed to a home screen *and* opened in a browser tab, which is two tabs,
 * and the single-tab manager silently refuses persistence to the second one —
 * so the second tab is the one with no offline support and nothing says so.
 */
import { type FirebaseApp, initializeApp } from 'firebase/app';
import { type Auth, connectAuthEmulator, getAuth } from 'firebase/auth';
import {
  connectFirestoreEmulator,
  type Firestore,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
} from 'firebase/firestore';
import { connectFunctionsEmulator, type Functions, getFunctions } from 'firebase/functions';
import { connectStorageEmulator, type FirebaseStorage, getStorage } from 'firebase/storage';

const config = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

export const app: FirebaseApp = initializeApp(config);

export const db: Firestore = initializeFirestore(app, {
  localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
});

export const auth: Auth = getAuth(app);
export const storage: FirebaseStorage = getStorage(app);

/**
 * Callables live in `europe-west1`, and the region has to be named here.
 *
 * The default is `us-central1`, and a callable deployed to one region and
 * invoked in another fails with a CORS error in the browser console rather than
 * anything that mentions regions — an afternoon lost, every time, to a default.
 * Every function in `functions/src/` pins the same region; this is the other
 * half of that pin.
 */
export const functions: Functions = getFunctions(app, 'europe-west1');

/**
 * Emulators. One variable holds the host, and every service is derived from it
 * with the ports in firebase.json — three separate variables is three
 * chances to point half the app at production while debugging the other half.
 */
const emulatorHost = import.meta.env.VITE_FIREBASE_EMULATORS;
if (emulatorHost !== undefined && emulatorHost !== '') {
  const host = emulatorHost.split(':')[0] ?? 'localhost';
  connectFirestoreEmulator(db, host, 8080);
  connectStorageEmulator(storage, host, 9199);
  connectFunctionsEmulator(functions, host, 5001);
  // `disableWarnings` only silences the banner about sending credentials in the
  // clear, which against an emulator is the point.
  connectAuthEmulator(auth, `http://${host}:9099`, { disableWarnings: true });
}
